import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';
import { ALL_LOCATIONS } from '../../contexts/LocationContext';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import NotificationBell from './NotificationBell';
import LocationFormModal from '../locations/LocationFormModal';
import LocationImportModal from '../locations/LocationImportModal';
import BaaSigningGate from '../legal/BaaSigningGate';
import { Sun, Moon, LayoutDashboard, Users, Calendar, Map, FileText, Receipt, Brain, ChevronDown, ChevronUp, MapPin, Plus, Upload, Settings as SettingsIcon, ClipboardList, Video, MoreHorizontal, Send, Sparkles, Briefcase } from 'lucide-react';

const NAV_BY_TYPE = {
  medical_practice: [
    { to: '/app/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/app/contacts', label: 'Contacts', Icon: Users },
    { to: '/app/scheduling', label: 'Scheduling', Icon: Calendar },
    { to: '/app/telehealth', label: 'Telehealth', Icon: Video },
    { to: '/app/directory', label: 'Directory', Icon: Map },
    { to: '/app/referrals', label: 'Referrals', Icon: Send },
    { to: '/app/tbi-assessments', label: 'TBI Assessments', Icon: Brain },
    { to: '/app/forms', label: 'Forms', Icon: ClipboardList },
    { to: '/app/documents', label: 'Documents', Icon: FileText },
    { to: '/app/settings?tab=billing', label: 'Billing', Icon: Receipt },
  ],
  law_firm: [
    { to: '/app/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/app/cases', label: 'Cases', Icon: Briefcase },
    { to: '/app/contacts', label: 'Contacts', Icon: Users },
    { to: '/app/referrals/concierge', label: 'Concierge', Icon: Sparkles },
    { to: '/app/referrals', label: 'Referrals', Icon: Send },
    { to: '/app/scheduling', label: 'Calendar', Icon: Calendar },
    { to: '/app/directory', label: 'Directory', Icon: Map },
    { to: '/app/forms', label: 'Forms', Icon: ClipboardList },
    { to: '/app/documents', label: 'Documents', Icon: FileText },
  ],
};
// Marketers (invite-only, firm-like) get the law-firm navigation verbatim.
NAV_BY_TYPE.marketer = NAV_BY_TYPE.law_firm;

export default function AppShell() {
  const { profile, signOut } = useAuth();
  const { memberships, currentOrg, currentRole, switchOrg } = useOrg();
  const { locations, currentLocation, isAllLocations, switchLocation, restricted: locationRestricted } = useLocations();
  const [theme, setTheme] = useState(() => localStorage.getItem('pi-portal-theme') || 'light');
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [showLocationSwitcher, setShowLocationSwitcher] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [locationModal, setLocationModal] = useState(null); // 'new' | location | null
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Anyone in the org's staff/admin shell can manage locations from the
  // header. The API still gates writes server-side, so this is a UX
  // surface that mirrors the rest of the staff toolbar.
  const showLocationChrome = !!currentOrg && currentRole !== 'client';
  const canManageLocations = showLocationChrome;

  useSessionTimeout();

  const toggleTheme = () => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('pi-portal-theme', next);
      return next;
    });
  };

  // ── Priority nav ─────────────────────────────────────────────────
  // Labels are always on; tabs that don't fit the space between the logo
  // and the right controls collapse into a "More" menu. A hidden measuring
  // row (same markup) gives each tab's true width; a ResizeObserver on the
  // strip's container recomputes the split on every resize.
  const navItems = NAV_BY_TYPE[currentOrg?.type] || NAV_BY_TYPE.medical_practice;
  const navBoxRef = useRef(null);
  const navMeasureRef = useRef(null);
  const [navFit, setNavFit] = useState(navItems.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const routerLocation = useLocation();
  useEffect(() => { setMoreOpen(false); }, [routerLocation.pathname]);
  useLayoutEffect(() => {
    const box = navBoxRef.current;
    const meas = navMeasureRef.current;
    if (!box || !meas) return undefined;
    const MORE_W = 96;   // the "More ▾" pill
    const PAD = 12;      // strip padding + safety
    const compute = () => {
      const avail = box.clientWidth - PAD;
      const widths = Array.from(meas.children).map((c) => c.offsetWidth + 2);
      const total = widths.reduce((a, b) => a + b, 0);
      if (total <= avail) { setNavFit(widths.length); return; }
      let sum = 0;
      let n = 0;
      for (let i = 0; i < widths.length; i++) {
        if (sum + widths[i] <= avail - MORE_W) { sum += widths[i]; n += 1; } else break;
      }
      setNavFit(Math.max(1, n));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(box);
    return () => ro.disconnect();
  }, [navItems]);
  const navVisible = navItems.slice(0, navFit);
  const navOverflow = navItems.slice(navFit);

  const initials = profile
    ? `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase() || '??'
    : '??';

  const displayName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email
    : '';

  return (
    // Bounded app frame: the document itself never scrolls — each page
    // scrolls inside the outlet pane below. Keeps split-pane pages (map +
    // sidebar) from scrolling the whole viewport when a list overflows.
    <div className="app-frame overflow-hidden flex flex-col">
      <header className="flex items-center justify-between px-4 md:px-7 h-[60px] bg-secondary border-b border-theme shrink-0 relative z-50">
        {/* Left: burger + logo */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <button
            onClick={() => setMobileNav(!mobileNav)}
            className="lg:hidden w-9 h-9 rounded-[10px] bg-tertiary border border-theme flex items-center justify-center hover:bg-hover-surface transition-all"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              {mobileNav ? <><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></> : <><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="14" y2="8"/><line x1="2" y1="12" x2="14" y2="12"/></>}
            </svg>
          </button>
          <div className="flex items-center">
            <img src="/logo-vertical.svg" alt="MediLink" className="h-[34px] w-auto" />
          </div>
        </div>

        {/* Desktop nav — priority+overflow: labeled tabs while they fit,
            the rest in a More menu. navBoxRef spans the flexible middle of
            the header so the measurement reflects real available space. */}
        <div ref={navBoxRef} className="hidden lg:flex flex-1 min-w-0 justify-center px-4">
          {/* Hidden measuring row: same tab markup, never visible. */}
          <div ref={navMeasureRef} aria-hidden className="absolute -top-[999px] left-0 flex gap-0.5 invisible pointer-events-none">
            {navItems.map((item) => (
              <span key={item.to} className="px-3.5 py-[7px] text-[13px] font-medium flex items-center gap-1.5 whitespace-nowrap">
                <item.Icon size={14} strokeWidth={2.2} />
                {item.label}
              </span>
            ))}
          </div>

          <nav className="flex gap-0.5 bg-tertiary p-1 rounded-[10px] max-w-full">
            {navVisible.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.label}
                end={navItems.some((i) => i.to !== item.to && i.to.startsWith(item.to))}
                className={({ isActive }) =>
                  `px-3.5 py-[7px] rounded-[7px] text-[13px] font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${
                    isActive
                      ? 'bg-accent text-white shadow-glow'
                      : 'text-muted-c hover:text-primary-c'
                  }`
                }
              >
                <item.Icon size={14} strokeWidth={2.2} />
                {item.label}
              </NavLink>
            ))}
            {navOverflow.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setMoreOpen((v) => !v)}
                  className={`px-3.5 py-[7px] rounded-[7px] text-[13px] font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${
                    navOverflow.some((i) => routerLocation.pathname.startsWith(i.to))
                      ? 'bg-accent text-white shadow-glow'
                      : 'text-muted-c hover:text-primary-c'}`}
                >
                  <MoreHorizontal size={14} strokeWidth={2.2} /> More
                </button>
                {moreOpen && (
                  <div className="absolute left-0 top-full mt-2 w-52 bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50 animate-fade-in">
                    {navOverflow.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMoreOpen(false)}
                        className={({ isActive }) =>
                          `px-4 py-2.5 text-[13px] transition-all flex items-center gap-2.5 ${
                            isActive ? 'text-accent-light font-semibold' : 'text-secondary-c hover:bg-hover-surface'}`
                        }
                      >
                        <item.Icon size={14} strokeWidth={2.2} />
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {/* Org switcher */}
          {memberships.length > 1 && (
            <div className="relative">
              <button
                onClick={() => { setShowOrgSwitcher(!showOrgSwitcher); setShowLocationSwitcher(false); setShowUserMenu(false); }}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-tertiary border border-theme text-[12px] font-medium text-secondary-c hover:bg-hover-surface transition-all"
              >
                {currentOrg?.name || 'Select org'}
                {showOrgSwitcher ? <ChevronUp size={12} strokeWidth={2.5} /> : <ChevronDown size={12} strokeWidth={2.5} />}
              </button>
              {showOrgSwitcher && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50 animate-fade-in max-h-[60vh] overflow-y-auto">
                  {memberships.map(m => (
                    <button
                      key={m.org_id}
                      onClick={() => { switchOrg(m.org_id); setShowOrgSwitcher(false); }}
                      className={`w-full text-left px-4 py-2.5 text-[13px] transition-all ${
                        m.org_id === currentOrg?.id
                          ? 'bg-accent-glow text-accent-light font-semibold'
                          : 'text-secondary-c hover:bg-hover-surface'
                      }`}
                    >
                      {m.organization?.name || m.org_id}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Location chip — visible whenever the org has at least one
              location. Click to open a switcher menu. Single-location orgs
              still see the chip so the current location is always shown
              and the "+ Add new location" entry is one click away. */}
          {showLocationChrome && locations.length >= 1 && (
            <div className="relative">
              <button
                onClick={() => { setShowLocationSwitcher(!showLocationSwitcher); setShowOrgSwitcher(false); setShowUserMenu(false); setShowAddMenu(false); }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-tertiary border border-theme text-[12px] font-medium text-secondary-c hover:bg-hover-surface transition-all"
                title="Switch or add location"
              >
                <MapPin size={12} strokeWidth={2.2} />
                <span className="hidden sm:inline truncate max-w-[140px]">
                  {isAllLocations ? 'All locations' : (currentLocation?.name || 'Location')}
                </span>
                {showLocationSwitcher ? <ChevronUp size={12} strokeWidth={2.5} /> : <ChevronDown size={12} strokeWidth={2.5} />}
              </button>
              {showLocationSwitcher && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50 animate-fade-in">
                  {locations.length > 1 && !locationRestricted && (
                    <>
                      <button
                        onClick={() => { switchLocation(ALL_LOCATIONS); setShowLocationSwitcher(false); }}
                        className={`w-full text-left px-4 py-2.5 text-[13px] transition-all ${
                          isAllLocations
                            ? 'bg-accent-glow text-accent-light font-semibold'
                            : 'text-secondary-c hover:bg-hover-surface'
                        }`}
                      >
                        All locations
                      </button>
                      <hr className="border-theme my-1" />
                    </>
                  )}
                  {/* Scrollable list so many-location orgs don't run off-screen;
                      "All locations" above and "Add new location" below stay pinned. */}
                  <div className="max-h-[48vh] overflow-y-auto">
                    {locations.map(l => (
                      <button
                        key={l.id}
                        onClick={() => { switchLocation(l.id); setShowLocationSwitcher(false); }}
                        className={`w-full text-left px-4 py-2.5 text-[13px] transition-all flex items-center justify-between ${
                          !isAllLocations && l.id === currentLocation?.id
                            ? 'bg-accent-glow text-accent-light font-semibold'
                            : 'text-secondary-c hover:bg-hover-surface'
                        }`}
                      >
                        <span className="truncate">{l.name}</span>
                        {l.is_primary && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-c">Primary</span>
                        )}
                      </button>
                    ))}
                  </div>
                  {canManageLocations && (
                    <>
                      <hr className="border-theme my-1" />
                      <button
                        onClick={() => { setShowLocationSwitcher(false); setLocationModal('new'); }}
                        className="w-full text-left px-4 py-2.5 text-[13px] font-semibold text-accent-light hover:bg-hover-surface transition-all flex items-center gap-2"
                      >
                        <Plus size={13} strokeWidth={2.5} /> Add new location
                      </button>
                      <button
                        onClick={() => { setShowLocationSwitcher(false); setImportOpen(true); }}
                        className="w-full text-left px-4 py-2.5 text-[13px] font-semibold text-accent-light hover:bg-hover-surface transition-all flex items-center gap-2"
                      >
                        <Upload size={13} strokeWidth={2.5} /> Import from CSV
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Always-visible "+ Add" pill — fastest path to create another
              location. Hidden on the smallest screens where the icon-only
              location chip already covers the UI. */}
          {canManageLocations && (
            <div className="relative hidden sm:block">
              <button
                onClick={() => { setShowAddMenu(!showAddMenu); setShowLocationSwitcher(false); setShowOrgSwitcher(false); setShowUserMenu(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-strong bg-accent-glow text-accent-light text-[12px] font-semibold hover:bg-accent hover:text-white transition-all"
                title="Add location"
              >
                <Plus size={13} strokeWidth={2.5} />
                <span className="hidden xl:inline whitespace-nowrap">Add location</span>
                {showAddMenu ? <ChevronUp size={12} strokeWidth={2.5} /> : <ChevronDown size={12} strokeWidth={2.5} />}
              </button>
              {showAddMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50 animate-fade-in">
                  <button
                    onClick={() => { setShowAddMenu(false); setLocationModal('new'); }}
                    className="w-full text-left px-4 py-2.5 text-[13px] text-secondary-c hover:bg-hover-surface transition-all flex items-center gap-2"
                  >
                    <Plus size={13} strokeWidth={2.5} /> Add one location
                  </button>
                  <button
                    onClick={() => { setShowAddMenu(false); setImportOpen(true); }}
                    className="w-full text-left px-4 py-2.5 text-[13px] text-secondary-c hover:bg-hover-surface transition-all flex items-center gap-2"
                  >
                    <Upload size={13} strokeWidth={2.5} /> Import from CSV
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notifications */}
          <NotificationBell />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-[10px] border border-theme bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-accent-light transition-all hover:rotate-12"
          >
            {theme === 'dark' ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => { setShowUserMenu(!showUserMenu); setShowOrgSwitcher(false); }}
              className="flex items-center gap-2"
            >
              <div className="w-[34px] h-[34px] rounded-[10px] gradient-accent flex items-center justify-center text-[12px] font-semibold text-white">
                {initials}
              </div>
              <span className="hidden md:inline text-[13px] font-medium text-secondary-c whitespace-nowrap truncate max-w-[140px]">{displayName}</span>
            </button>
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-elevated border border-theme rounded-xl shadow-2xl py-1 z-50 animate-fade-in">
                <NavLink
                  to="/app/settings"
                  onClick={() => setShowUserMenu(false)}
                  className="block px-4 py-2.5 text-[13px] text-secondary-c hover:bg-hover-surface transition-all"
                >
                  Settings
                </NavLink>
                {profile?.is_platform_admin && (
                  <NavLink
                    to="/admin"
                    onClick={() => setShowUserMenu(false)}
                    className="block px-4 py-2.5 text-[13px] text-secondary-c hover:bg-hover-surface transition-all"
                  >
                    Platform Admin
                  </NavLink>
                )}
                <hr className="border-theme my-1" />
                <button
                  onClick={() => { setShowUserMenu(false); signOut(); }}
                  className="w-full text-left px-4 py-2.5 text-[13px] text-danger hover:bg-hover-surface transition-all"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Close dropdowns on click outside */}
      {(showOrgSwitcher || showLocationSwitcher || showUserMenu) && (
        <div className="fixed inset-0 z-40" onClick={() => { setShowOrgSwitcher(false); setShowLocationSwitcher(false); setShowUserMenu(false); }} />
      )}

      {/* Mobile nav drawer */}
      {mobileNav && (
        <>
          <div className="fixed inset-0 bg-overlay z-40 lg:hidden" onClick={() => setMobileNav(false)} />
          <div className="fixed top-[60px] left-0 right-0 bg-secondary border-b border-theme z-50 lg:hidden animate-fade-in max-h-[calc(100dvh-60px)] overflow-y-auto">
            <nav className="flex flex-col py-2">
              {(NAV_BY_TYPE[currentOrg?.type] || NAV_BY_TYPE.medical_practice).map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileNav(false)}
                  className={({ isActive }) =>
                    `px-6 py-3 text-[14px] font-medium transition-all border-l-2 flex items-center gap-2.5 ${
                      isActive ? 'text-accent-light border-accent bg-accent-glow' : 'text-secondary-c border-transparent hover:bg-hover-surface'
                    }`
                  }
                >
                  <item.Icon size={16} strokeWidth={2} />
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to="/app/settings"
                onClick={() => setMobileNav(false)}
                className={({ isActive }) =>
                  `px-6 py-3 text-[14px] font-medium transition-all border-l-2 flex items-center gap-2.5 ${
                    isActive ? 'text-accent-light border-accent bg-accent-glow' : 'text-secondary-c border-transparent hover:bg-hover-surface'
                  }`
                }
              >
                <SettingsIcon size={16} strokeWidth={2} />
                Settings
              </NavLink>
            </nav>
          </div>
        </>
      )}

      {/* Scrolling pane for the page. Document-flow pages scroll here;
          pages with their own overflow-y-auto root or a viewport-sized
          root (Directory, Scheduler) fill it exactly and it stays put. */}
      <div className="app-scroll flex-1 min-h-0 flex flex-col">
        <Outlet />
      </div>

      <LocationFormModal editing={locationModal} onClose={() => setLocationModal(null)} />
      {importOpen && <LocationImportModal onClose={() => setImportOpen(false)} />}

      {/* Renders nothing unless this org has no executed BAA and this user is
          the one who can sign it. See BaaSigningGate for why it blocks. */}
      <BaaSigningGate>{null}</BaaSigningGate>
    </div>
  );
}
